/**
 * Client of the directory users search (docs/contract.md, CR-34): GET /api/v1/directory/users
 * is the read-only Keycloak realm search behind the calendar event form's participant picker.
 * Same-origin calls like the rooms client (the Vite dev server proxies /api to the control
 * service, production serves the SPA same-origin); the bearer is the SPA's SSO session access
 * token. The endpoint is OPTIONAL on deployments: without the directory client secret it stays
 * dark (503 directory_disabled), so the picker treats every failure as "degrade to free text".
 */

/** One directory user of a search result (CR-34 wire shape). */
export interface DirectoryUser {
  readonly id: string;
  readonly username: string;
  /**
   * Composed by the server from firstName/lastName (space-joined) with a username fallback,
   * so it is never empty for a real realm user; the client clamps defensively anyway.
   */
  readonly fullName: string;
  /** Empty string when the realm user has no e-mail. */
  readonly email: string;
}

/** Failure of the directory endpoint: HTTP error, network failure or malformed response body. */
export class DirectoryApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'DirectoryApiError';
    this.status = status;
    this.code = code;
  }
}

/** The server's q bound: 1..100 characters after trimming (CR-34); the client clamps to 100. */
export const DIRECTORY_MAX_QUERY_RUNES = 100;

/** The server's limit bound: values above 50 are capped server-side (CR-34); default is 20. */
export const DIRECTORY_MAX_LIMIT = 50;

/** The server's ids bound: at most 50 comma-separated identifiers per by-ids request (CR-35). */
export const DIRECTORY_MAX_IDS = 50;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrows one user row; id is required, the display fields clamp to '' (server owns defaults). */
export function parseDirectoryUser(value: unknown): DirectoryUser {
  if (!isRecord(value)) {
    throw new TypeError('directory user must be a JSON object');
  }
  if (typeof value.id !== 'string' || value.id.length === 0) {
    throw new TypeError('directory user field "id" must be a non-empty string');
  }
  const text = (raw: unknown): string => (typeof raw === 'string' ? raw : '');
  return {
    id: value.id,
    username: text(value.username),
    fullName: text(value.fullName),
    email: text(value.email),
  };
}

/**
 * Narrows the unknown search response to {@link DirectoryUser}[]: `users` is always an array on
 * the wire (empty when nothing matches); a shape violation raises TypeError naming the field.
 */
export function parseDirectoryUsers(body: unknown): DirectoryUser[] {
  if (!isRecord(body)) {
    throw new TypeError('directory search response must be a JSON object');
  }
  if (!Array.isArray(body.users)) {
    throw new TypeError('directory search response field "users" must be an array');
  }
  return body.users.map(parseDirectoryUser);
}

/** Builds the DirectoryApiError for a non-2xx response, reading the contract envelope when present. */
async function errorFromResponse(response: Response, endpoint: string): Promise<DirectoryApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON error body, keep the generic message.
  }
  let code = 'unknown_error';
  let message = `${endpoint} request failed with HTTP ${response.status}`;
  if (isRecord(body) && isRecord(body.error)) {
    if (typeof body.error.code === 'string') {
      code = body.error.code;
    }
    if (typeof body.error.message === 'string') {
      message = body.error.message;
    }
  }
  return new DirectoryApiError(response.status, code, message);
}

/** Clips by runes: the server bounds q in runes, not UTF-16 units. */
function clampRunes(value: string, max: number): string {
  return Array.from(value)
    .slice(0, max)
    .join('');
}

/**
 * Searches the realm's real users (CR-34). Client-side guards mirror the server: an empty
 * (after trimming) query answers [] WITHOUT a request, and a query past 100 runes is clamped
 * to 100. `limit` clamps into 1..50 when provided and is omitted otherwise (the server default
 * 20 applies). Errors: 400 invalid_request, 401 (the bearer was rejected - the picker reads any
 * failure as degrade), 502 directory_unavailable, 503 directory_disabled, plus network_error /
 * invalid_response for transport and shape violations.
 */
export async function searchDirectoryUsers(
  token: string,
  query: string,
  opts?: { readonly limit?: number },
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<DirectoryUser[]> {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return [];
  }
  const params = new URLSearchParams({ q: clampRunes(trimmed, DIRECTORY_MAX_QUERY_RUNES) });
  if (opts?.limit !== undefined && Number.isFinite(opts.limit)) {
    params.set('limit', `${Math.min(DIRECTORY_MAX_LIMIT, Math.max(1, Math.floor(opts.limit)))}`);
  }
  let response: Response;
  try {
    response = await fetchImpl(`/api/v1/directory/users?${params.toString()}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new DirectoryApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'directory search');
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new DirectoryApiError(
      response.status,
      'invalid_response',
      'directory search response is not JSON',
    );
  }
  try {
    return parseDirectoryUsers(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed directory response';
    throw new DirectoryApiError(response.status, 'invalid_response', detail);
  }
}

/**
 * Looks up display names by realm ids (CR-35): GET /api/v1/directory/users/by-ids?ids=a,b,c
 * behind the same SSO bearer, the same user rows and the same error semantics as
 * {@link searchDirectoryUsers} (400 invalid_request, 502 directory_unavailable,
 * 503 directory_disabled, plus network_error / invalid_response). The lookup answers only
 * the ids that still exist - deleted ones are silently absent from the result. Client-side
 * guards mirror the server: empty ids answer [] WITHOUT a request, duplicates collapse, and
 * a list past 50 ids is sliced to the first 50 (the server would 400 the rest).
 */
export async function fetchUsersByIds(
  token: string,
  ids: readonly string[],
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<DirectoryUser[]> {
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (id.length > 0 && !seen.has(id)) {
      seen.add(id);
      unique.push(id);
    }
    if (unique.length >= DIRECTORY_MAX_IDS) {
      break;
    }
  }
  if (unique.length === 0) {
    return [];
  }
  const params = new URLSearchParams({ ids: unique.join(',') });
  let response: Response;
  try {
    response = await fetchImpl(`/api/v1/directory/users/by-ids?${params.toString()}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new DirectoryApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'directory by-ids');
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new DirectoryApiError(
      response.status,
      'invalid_response',
      'directory by-ids response is not JSON',
    );
  }
  try {
    return parseDirectoryUsers(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed directory response';
    throw new DirectoryApiError(response.status, 'invalid_response', detail);
  }
}
